import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import Swal from 'sweetalert2';
import { OrgContextService } from '../services/org-context.service';
import { OnboardingService } from '../services/onboarding.service';

/**
 * Blocks routes that only make sense for an organisation connected to
 * Planning Center — currently /automations, whose triggers are all PCO
 * webhook events.
 *
 * Hiding the sidebar link is not enough on its own: a bookmark, a link in an
 * old email, or simply typing the URL would otherwise land a standalone
 * organisation on a page that cannot work.
 *
 * Usage: canActivate: [pcoGuard()]
 */
export function pcoGuard(): CanActivateFn {
    return async () => {
        const router = inject(Router);
        const orgContext = inject(OrgContextService);

        try {
            await orgContext.load();

            if (orgContext.hasPco()) {
                return true;
            }

            await Swal.fire({
                icon: 'info',
                title: 'Not available',
                text: 'This feature needs a Planning Center connection, which your organization does not have.',
                confirmButtonColor: '#556ee6'
            });

            await router.navigate(['/dashboard']);
            return false;
        } catch (error) {
            // Fail towards Planning Center, matching OrgContextService: an
            // existing PCO org losing access to Automations over a transient
            // read failure is the worse outcome.
            console.error('PCO guard error:', error);
            return true;
        }
    };
}

/**
 * Guards an onboarding step that only exists for Planning Center
 * organisations — currently /webhook-setup, which subscribes to PCO webhooks.
 *
 * A standalone organisation's step list already excludes it, so nothing routes
 * them here. This covers the ways someone arrives without being routed: a
 * bookmark, the browser's back button, or a link in an older email.
 *
 * Unlike pcoGuard this redirects silently to whatever step the organisation
 * should be on instead of showing a dialog and going to the dashboard. Someone
 * mid-wizard did not choose to come here, and bouncing them to a dashboard
 * they have not finished onboarding for would only bounce them back again.
 */
export function pcoSetupGuard(): CanActivateFn {
    return async () => {
        const router = inject(Router);
        const auth = inject(Auth);
        const orgContext = inject(OrgContextService);
        const onboardingService = inject(OnboardingService);

        try {
            await orgContext.load();
            if (orgContext.hasPco()) return true;

            const orgId = orgContext.orgId();
            if (orgId) {
                const next = await onboardingService.resolveNextStep(orgId);
                await router.navigate([next ? next.route : '/dashboard']);
            } else {
                await router.navigate(['/dashboard']);
            }

            return false;
        } catch (error) {
            console.error('PCO setup guard error:', error);
            return true;
        }
    };
}
